import React, { useCallback, useMemo, useState, useEffect } from "react";
import {
  View,
  TouchableOpacity,
  Modal,
  TouchableWithoutFeedback,
  Pressable,
} from "react-native";
import StyledText from "../../../common/ui/styled-text";
import { useSettingsData } from "../../../../contexts/SettingsDataContext";
import SettingsItem from "../common/settings-item";
import { formatISO } from "date-fns";
import dayjs from "../../../../utils/dayjs";
import { Calendar } from "react-date-range";
import TimeInput from "./time-input";
import { useFormattedDate } from "../../../../hooks/useFormattedDate";
import {
  X as XIcon,
  Calendar as CalendarIcon,
  MessageSquare as MessageSquareIcon,
} from "lucide-react-native";
import { Clock as ClockIcon } from "lucide-react-native";
import { useIsTemporarilyClosed } from "../../../../hooks/useIsTemporarilyClosed";
import { Input } from "../../../common/ui/input.native";
import { usePostHog } from "posthog-js/react";

export default function TemporaryClosure() {
  const { settings, handleSaveTemporaryClosure } = useSettingsData();
  const posthog = usePostHog();
  const [showModal, setShowModal] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [tempUntil, setTempUntil] = useState<string | null>(
    settings.tempClosedUntil
  );
  const initialGreeting = useMemo(
    () =>
      settings.greetingMessageTempClosed?.length
        ? settings.greetingMessageTempClosed
        : settings.greetingMessageClosed || "",
    [settings.greetingMessageTempClosed, settings.greetingMessageClosed]
  );
  const [greeting, setGreeting] = useState<string>(initialGreeting);
  const isTempClosed = useIsTemporarilyClosed();

  const { formatDateTimeLong, timezone } = useFormattedDate();

  // storeTz() => now in store timezone (or system tz if none provided)
  // storeTz(value) => that value in store timezone
  const storeTz = useCallback(
    (value?: any) => (timezone ? dayjs(value).tz(timezone) : dayjs(value)),
    [timezone]
  );

  const formatPresetPreview = useCallback(
    (d: Date) => storeTz(d).format("ddd, MMM D • h:mm A"),
    [storeTz]
  );

  const formatCalendarDate = useCallback(
    (d: Date | null) => (d ? storeTz(d).format("MMMM D, YYYY") : ""),
    [storeTz]
  );

  const getPresetPreview = useCallback(
    (getDate: () => Date) => formatPresetPreview(getDate()),
    [formatPresetPreview]
  );

  const presetDates = useMemo(() => {
    const baseNow = storeTz();
    return [
      {
        label: "Next 6 hours",
        getDate: () => baseNow.add(6, "hour").second(0).millisecond(0).toDate(),
      },
      {
        label: "Next 12 hours",
        getDate: () =>
          baseNow.add(12, "hour").second(0).millisecond(0).toDate(),
      },
      {
        label: "Until tomorrow",
        getDate: () => {
          const d = storeTz()
            .add(1, "day")
            .hour(5)
            .minute(0)
            .second(0)
            .millisecond(0);
          return d.toDate();
        },
      },
      {
        label: "Until Monday",
        getDate: () => {
          const nowTz = storeTz();
          const day = nowTz.day();
          let offset = (1 - day + 7) % 7; // days until Monday
          if (offset === 0) offset = 7; // next Monday if already Monday
          return nowTz
            .add(offset, "day")
            .hour(5)
            .minute(0)
            .second(0)
            .millisecond(0)
            .toDate();
        },
      },
    ];
  }, [showModal, storeTz]);

  const clearClosure = () => {
    setTempUntil(null);
    setGreeting("");
    handleSaveTemporaryClosure(null, "");
  };

  const openModal = () => {
    posthog.capture("temporary_closure_modal_opened", {
      isCurrentlyClosed: isTempClosed,
    });
    // Initialize draft state from saved settings so in-progress edits don't leak outside
    const base = isTempClosed ? storeTz(settings.tempClosedUntil) : storeTz();
    const initial = isTempClosed ? base : base.add(1, "hour");
    setSelectedDate(initial.second(0).millisecond(0).toDate());
    setTempUntil(settings.tempClosedUntil || null);
    setGreeting(initialGreeting);
    setShowModal(true);
  };

  const [selectedDate, setSelectedDate] = useState<Date>(() =>
    tempUntil ? storeTz(tempUntil).toDate() : storeTz().toDate()
  );

  const handleDateChange = (date: Date) => {
    const currentTz = storeTz(selectedDate);
    const pickedTz = storeTz()
      .year(date.getFullYear())
      .month(date.getMonth())
      .date(date.getDate())
      .hour(0)
      .minute(0)
      .second(0)
      .millisecond(0);
    const merged = pickedTz
      .hour(currentTz.hour())
      .minute(currentTz.minute())
      .second(0)
      .millisecond(0);
    setSelectedDate(merged.toDate());
  };

  const handleTimeChange = (timeString: string) => {
    const [h, m] = timeString.split(":");
    if (!h || !m) return;
    const updated = storeTz(selectedDate)
      .hour(parseInt(h, 10))
      .minute(parseInt(m, 10))
      .second(0)
      .millisecond(0);
    setSelectedDate(updated.toDate());
  };

  const handlePresetClick = (date: Date) => {
    setSelectedDate(date);
  };

  // Compute whether selected date/time is in the past (store timezone)
  const nowTz = storeTz();
  const selectedTz = storeTz(selectedDate);
  const isPastSelection = selectedTz.isBefore(nowTz);

  const handleSubmit = () => {
    if (isPastSelection) return; // guard, shouldn't happen if UI blocks
    setGreeting((greeting) => {
      return greeting.trim();
    });
    handleSaveTemporaryClosure(tempUntil, greeting.trim());
    setShowModal(false);
  };

  useEffect(() => {
    if (!selectedDate) return;
    setTempUntil(formatISO(selectedDate));
  }, [selectedDate]);

  return (
    <View className="mb-5">
      <SettingsItem
        title="Temporary Store Closure"
        description="Temporarily shut down the store due to breaks or holidays. Loman will still receive calls."
      >
        <View className="mb-4">
          {isTempClosed ? (
            <View className="rounded-lg border bg-muted/50 p-4 flex-col sm:flex-row sm:items-center justify-start">
              <View className="flex-1">
                <View className="mb-2 flex-row items-center gap-2">
                  <CalendarIcon size="16" className="flex-none" />
                  <StyledText className="font-medium">
                    Store Closed Until
                  </StyledText>
                  <StyledText className="text-sm text-gray-500">
                    {settings.tempClosedUntil
                      ? formatDateTimeLong(settings.tempClosedUntil)
                      : ""}
                  </StyledText>
                </View>
                <View className="flex-row items-start gap-2">
                  <MessageSquareIcon size="16" className="flex-none mt-0.5" />
                  <View className="flex-1">
                    <StyledText className="block font-medium mb-1">
                      Closed Store Greeting
                    </StyledText>
                    <StyledText className="text-sm text-gray-500 max-w-md">
                      "
                      {settings.greetingMessageTempClosed?.length
                        ? settings.greetingMessageTempClosed
                        : settings.greetingMessageClosed || ""}
                      "
                    </StyledText>
                  </View>
                </View>
              </View>
              <View className="flex-row items-center mt-3 sm:mt-0 sm:ml-4">
                <TouchableOpacity
                  onPress={openModal}
                  className="px-3 py-2 bg-gray-100 rounded"
                >
                  <StyledText>Edit</StyledText>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={clearClosure}
                  className="ml-2 px-3 py-2 bg-red-100 rounded"
                >
                  <StyledText className="text-red-600">Clear</StyledText>
                </TouchableOpacity>
              </View>
            </View>
          ) : (
            <TouchableOpacity
              onPress={openModal}
              className="w-full px-4 py-3 bg-amber-100 hover:bg-amber-200 border border-amber-200 rounded-lg"
            >
              <StyledText className="text-center text-amber-800 font-medium">
                Activate Temporary Closure
              </StyledText>
            </TouchableOpacity>
          )}
        </View>

        {isTempClosed && (
          <View className="bg-yellow-50 rounded p-4 py-6 mb-2">
            <StyledText className="text-yellow-800">
              Hours are disabled while temporary closure is active. To change
              store hours, stop the temporary closure first.
            </StyledText>
          </View>
        )}

        <Modal
          visible={showModal}
          transparent
          animationType="none"
          onRequestClose={() => setShowModal(false)}
        >
          <TouchableWithoutFeedback onPress={() => setShowModal(false)}>
            <View className="flex-1 bg-black/50 justify-center items-center">
              <TouchableWithoutFeedback>
                <View className="bg-white rounded-xl p-5 w-full sm:w-4/5 max-w-[600px]">
                  <TouchableOpacity
                    className="absolute top-2 right-2 cursor-pointer z-50 p-2"
                    onPress={() => setShowModal(false)}
                  >
                    <XIcon size={20} color="#6b7280" />
                  </TouchableOpacity>
                  <StyledText className="text-lg font-bold text-center mb-4 z-40">
                    Temporary Store Closure
                  </StyledText>

                  <View className="space-y-4">
                    <View className="space-y-2">
                      <StyledText className="text-sm font-medium">
                        Quick Select
                      </StyledText>
                      <View className="flex-row flex-wrap -m-1">
                        {presetDates.map((preset) => (
                          <View key={preset.label} className="basis-1/2 p-1">
                            <Pressable
                              onPress={() =>
                                handlePresetClick(preset.getDate())
                              }
                              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-gradient-to-b from-gray-50 to-white hover:from-white hover:to-gray-50 shadow-sm active:opacity-90"
                            >
                              <View className="flex-row items-center gap-2">
                                <ClockIcon size={14} color="#6b7280" />
                                <View className="flex">
                                  <StyledText className="text-[13px] font-medium text-gray-800">
                                    {preset.label}
                                  </StyledText>
                                  <StyledText className="text-[11px] text-gray-500 leading-tight">
                                    {getPresetPreview(preset.getDate)}
                                  </StyledText>
                                </View>
                              </View>
                            </Pressable>
                          </View>
                        ))}
                      </View>
                    </View>

                    <View className="h-px bg-gray-200 my-1" />

                    <View className="space-y-2">
                      <View className="flex-row items-start gap-4">
                        <View className="flex-1">
                          <StyledText className="text-sm font-medium mb-2">
                            Store Reopens
                          </StyledText>
                          <TouchableOpacity
                            onPress={() => setShowCalendar(!showCalendar)}
                            className={`w-full h-11 rounded-md border px-3 py-2 bg-white flex-row items-center gap-2 shadow-sm ${
                              showCalendar
                                ? "border-blue-500 ring-2 ring-blue-500"
                                : "border-gray-200"
                            }`}
                          >
                            <StyledText className="text-sm flex-1">
                              {selectedDate
                                ? formatCalendarDate(selectedDate)
                                : "Select date"}
                            </StyledText>
                            <CalendarIcon className="w-4 h-4 text-gray-500" />
                          </TouchableOpacity>
                        </View>

                        <View>
                          <StyledText className="text-sm font-medium mb-2">
                            Time
                          </StyledText>
                          <TimeInput
                            className="h-11 w-32"
                            time={selectedTz.format("HH:mm")}
                            setTime={handleTimeChange}
                            disabled={false}
                            showInvalid={isPastSelection}
                          />
                        </View>
                      </View>
                      {isPastSelection && (
                        <StyledText className="text-xs text-red-600">
                          Selected time is in the past. Please choose a future
                          time.
                        </StyledText>
                      )}
                      <StyledText className="text-xs text-gray-500">
                        Times selected are in your store's timezone.
                      </StyledText>
                    </View>

                    <View className="space-y-2">
                      <StyledText className="text-sm font-medium">
                        Closed Store Greeting
                      </StyledText>
                      <Input
                        multiline
                        numberOfLines={4}
                        className="min-h-[100px] border border-gray-200 rounded-md px-3 py-2 placeholder-gray-500"
                        placeholder="We're temporarily closed for maintenance. Please check back soon!"
                        value={greeting}
                        onChangeText={setGreeting}
                      />
                      <StyledText className="text-xs text-gray-500">
                        This message will greet visitors when they call your
                        store while it's closed.
                      </StyledText>
                    </View>

                    <View className="flex-row gap-3 pt-4">
                      <Pressable
                        onPress={handleSubmit}
                        disabled={
                          !selectedDate || !greeting?.trim() || isPastSelection
                        }
                        className={`flex-1 px-4 py-2 rounded-md ${!selectedDate || !greeting?.trim() || isPastSelection ? "opacity-50" : "bg-blue-500"}`}
                      >
                        <StyledText
                          className={`text-center ${!selectedDate || !greeting?.trim() || isPastSelection ? "text-gray-700" : "text-white"}`}
                        >
                          Save Closure
                        </StyledText>
                      </Pressable>

                      <Pressable
                        onPress={() => setShowModal(false)}
                        className="flex-1 px-4 py-2 rounded-md border border-gray-200"
                      >
                        <StyledText className="text-center">Cancel</StyledText>
                      </Pressable>
                    </View>
                  </View>
                </View>
              </TouchableWithoutFeedback>
            </View>
          </TouchableWithoutFeedback>

          {showCalendar && (
            <Modal
              visible={showCalendar}
              transparent={true}
              animationType="none"
              onRequestClose={() => setShowCalendar(false)}
            >
              <TouchableWithoutFeedback onPress={() => setShowCalendar(false)}>
                <View className="flex-1 bg-black/30 justify-center items-center">
                  <TouchableWithoutFeedback>
                    <View className="bg-white rounded-xl p-4 shadow-lg">
                      <Calendar
                        date={selectedDate || new Date()}
                        onChange={(date) => {
                          handleDateChange(date);
                          setShowCalendar(false);
                        }}
                        minDate={new Date()}
                        color="#1F2937"
                        className="w-full"
                      />
                    </View>
                  </TouchableWithoutFeedback>
                </View>
              </TouchableWithoutFeedback>
            </Modal>
          )}
        </Modal>
      </SettingsItem>
    </View>
  );
}
